import * as yup from 'yup'

import { isLoadBalancerCurrentValid } from '../../domain/load-balancer-rules'
import { CHARGEPOINT_MIN_CURRENT } from '../../domain/electrical-settings'
import {
    useLoadbalancerOfflineCurrentSchema,
    useLoadbalancerOfflinePhaseSchema,
} from '../../hooks/useCapiFormValidation'
import { ChargePointImport } from '../../redux/api/web-api'
import { isAuraImport } from '../../../lib/isAura'
import { MAX_NAME_LENGTH, MAX_ORG_DESC_LENGTH } from '../../constants/InputsLength'
import useText from '../../hooks/useText'

export const nameRegex = /^(?!\s).*/
export const nameValidation = yup.string().required()

export const LBgroupDescriptionValidation = yup.string().max(MAX_ORG_DESC_LENGTH)

export const useLoadbalancerElectricalSettingsSchema = () => {
    const text = useText()
    return (md: boolean) => {
        const baseSchema = yup.object().shape({
            minCurrentPerChargePoint: yup
                .number()
                .transform((value) => (isNaN(value) ? undefined : value))
                .min(CHARGEPOINT_MIN_CURRENT, text('msgInstallationWizardAddLBMinCurrentPerChargerTooLowError'))
                .required(text('msgInstallationWizardAddLBMinCurrentPerChargerTooLowError')),
            schukoEnabled: yup.boolean(),

            maxCurrentPerPhase: yup
                .number()
                .transform((value) => (isNaN(value) ? undefined : value))
                .test('less-than-min-current', function (value) {
                    if (!isLoadBalancerCurrentValid(value)) {
                        return this.createError({
                            path: this.path,
                            message: text('msgInstallationWizardAddLBMinCurrentPerChargerTooLowError'),
                        })
                    }
                    return true
                })

                .required(text('msgInstallationWizardErrorMaxCurrentPerPhaseRequired')),
        })
        if (md) {
            return baseSchema.shape({
                supplyMaxCurrent: yup
                    .number()
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .required(text('msgInstallationWizardConfigLoadbalancerErrorSupplyCurrent'))
                    .min(0, text('msgInstallationWizardConfigLoadbalancerErrorSupplyCurrenttoolow')),

                deviceControlledMaxCurrentPerPhase: yup
                    .number()
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .test('less-than-min-current', function (value) {
                        if (!isLoadBalancerCurrentValid(value)) {
                            return this.createError({
                                path: this.path,
                                message: text('msgInstallationWizardAddLBMinCurrentPerChargerTooLowError'),
                            })
                        }
                        return true
                    })
                    .required(text('msgInstallationWizardErrorMaxCurrentPerPhaseRequired')),
                maxCurrentPerPhase: yup
                    .number()
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .test('less-than-min-current', function (value) {
                        if (value && !isLoadBalancerCurrentValid(value, true)) {
                            return this.createError({
                                path: this.path,
                                message: text('msgMaxCurrentPerPhaseWithMDTooLowError'),
                            })
                        }
                        return true
                    })

                    .required(text('msgInstallationWizardErrorMaxCurrentPerPhaseRequired')),
            })
        }

        return baseSchema
    }
}

export const useLoadbalancerSchema = () => {
    const text = useText()
    const loadbalancerSettingsSchema = useLoadbalancerElectricalSettingsSchema()

    return (measurementDevice: boolean) => {
        return loadbalancerSettingsSchema(measurementDevice).shape({
            name: nameValidation
                .matches(nameRegex, text('msgWhiteSpacesValidation'))
                .max(MAX_NAME_LENGTH, text('lblNameError')),
            companyId: yup.string().required(text('lblOrganisationNameRequired')),
        })
    }
}

export const useLoadBalancerOfflineSettingsValidation = (
    chargePoint: ChargePointImport,
    minValue: number | undefined
) => {
    const text = useText()

    const loadbalancerOfflineCurrentSchema = useLoadbalancerOfflineCurrentSchema()
    const loabalancnerOfflienPhaseSchema = useLoadbalancerOfflinePhaseSchema()

    return yup.object().shape({
        lbc_1: loadbalancerOfflineCurrentSchema(
            1,
            (chargePoint.connectors && chargePoint.connectors[0]?.name) ?? ''
        ).test('less-than-installation-current', function (value) {
            if (value && chargePoint.connectors && minValue) {
                if (value > minValue) {
                    return this.createError({
                        path: this.path,
                        message: text('lblInstallationCurrentOfflineValidationMax', {
                            connectorType: chargePoint.connectors[0]?.name,
                            installationCurrent: minValue,
                        }),
                    })
                }
            }

            return true
        }),
        lbOfflinePhase_1: loabalancnerOfflienPhaseSchema(chargePoint.connectors && chargePoint.connectors[0]?.name),
        ...(isAuraImport(chargePoint) && {
            lbc_2: loadbalancerOfflineCurrentSchema(
                2,
                (chargePoint.connectors && chargePoint.connectors[1]?.name) ?? ''
            ).test('less-than-installation-current', function (value) {
                if (value && chargePoint.connectors && minValue) {
                    if (value > minValue) {
                        return this.createError({
                            path: this.path,
                            message: text('lblInstallationCurrentOfflineValidationMax', {
                                connectorType: chargePoint.connectors[1]?.name,
                                installationCurrent: minValue,
                            }),
                        })
                    }
                }

                return true
            }),
            lbOfflinePhase_2: loabalancnerOfflienPhaseSchema(chargePoint.connectors && chargePoint.connectors[1]?.name),
        }),
    })
}
